import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function normalizePlatformRole(v) {
  const r = safeString(v).trim().toUpperCase();
  if (r === "SUPER_ADMIN" || r === "ADMIN" || r === "SUPPORT") return r;
  return "";
}

function roleLabel(role) {
  const r = safeString(role).trim().toUpperCase();
  if (r === "SUPER_ADMIN") return "Super Admin";
  if (r === "ADMIN") return "Admin";
  if (r === "SUPPORT") return "Support";
  return "User";
}

function formatNumber(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0";
  return x.toLocaleString();
}

function AttentionRow({ title, body, tone = "neutral" }) {
  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: 16,
        padding: "14px 16px",
        background:
          tone === "warning"
            ? "rgba(249,155,85,0.08)"
            : tone === "danger"
            ? "rgba(239,68,68,0.06)"
            : tone === "success"
            ? "rgba(34,197,94,0.06)"
            : "rgba(0,0,0,0.02)",
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 900,
          color: "var(--ink)",
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </div>
      <div
        style={{
          marginTop: 6,
          fontSize: 13,
          lineHeight: 1.45,
          color: "var(--muted)",
        }}
      >
        {body}
      </div>
    </div>
  );
}

export default function PlatformAdminHome() {
  const nav = useNavigate();
  const loc = useLocation();
  const auth = useAuth();

  const token = safeString(auth?.token).trim();
  const isAuthed = !!token;

  const inPlatformRoute = loc.pathname.startsWith("/platform");
  const platformRole = normalizePlatformRole(auth?.user?.platformRole ?? auth?.platformRole ?? "");
  const hasPlatformAccess = !!platformRole;

  const apiGet = auth?.apiGet;

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    businessUnits: null,
    platformUsers: null,
    appUsers: null,
  });

  useEffect(() => {
    let alive = true;

    async function run() {
      if (!isAuthed || !hasPlatformAccess || !inPlatformRoute) return;
      if (typeof apiGet !== "function") return;

      setLoading(true);
      try {
        const blob = await apiGet("/api/platform/stats");
        if (!alive) return;

        setStats({
          businessUnits: blob?.businessUnits ?? null,
          platformUsers: blob?.platformUsers ?? null,
          appUsers: blob?.appUsers ?? null,
        });
      } catch {
        // keep graceful placeholder state
      } finally {
        if (alive) setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [apiGet, hasPlatformAccess, inPlatformRoute, isAuthed]);

  const attentionItems = useMemo(() => {
    const buCount = Number(stats.businessUnits ?? 0);
    const platformUserCount = Number(stats.platformUsers ?? 0);
    const appUserCount = Number(stats.appUsers ?? 0);

    return [
      {
        title: "Billing alerts not wired yet",
        body: "Trials ending, past-due accounts, and expiring stores should surface here next. This will become your daily action area.",
        tone: "warning",
      },
      {
        title: buCount > 0 ? `${buCount} business units currently loaded` : "No business units found",
        body:
          buCount > 0
            ? "Your platform structure is live. Next step is attaching employments and making billing / usage visible at the Business Unit level."
            : "Create your first Business Unit to begin assigning users, employments, and billing records.",
        tone: buCount > 0 ? "success" : "warning",
      },
      {
        title:
          platformUserCount > 0
            ? `${platformUserCount} platform user${platformUserCount === 1 ? "" : "s"} with access`
            : "No platform users found",
        body:
          platformUserCount > 0
            ? "Keep Super Admin count minimal. Support and Admin roles should cover most non-founder access."
            : "Platform access has not been assigned yet. Create or promote at least one admin user.",
        tone: platformUserCount > 0 ? "neutral" : "danger",
      },
      {
        title:
          appUserCount > 0
            ? `${appUserCount} app user${appUserCount === 1 ? "" : "s"} in system`
            : "No app users found yet",
        body:
          appUserCount > 0
            ? "Store staff identities are present. The next priority is making employments, permissions, and support tooling easier to manage."
            : "Once stores are added, staff identities and employments should begin showing here.",
        tone: appUserCount > 0 ? "neutral" : "neutral",
      },
    ];
  }, [stats]);

  if (!isAuthed) {
    return (
      <div className="ft-page">
        <div className="ft-header">
          <div className="ft-header__text">
            <h1 className="ft-title">Platform</h1>
            <p className="ft-subtitle">Please sign in to continue.</p>
          </div>
          <div className="ft-header__actions">
            <button className="ft-btn ft-btn--primary" type="button" onClick={() => nav("/login")}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasPlatformAccess) {
    return (
      <div className="ft-page">
        <div className="ft-header">
          <div className="ft-header__text">
            <h1 className="ft-title">Platform</h1>
            <p className="ft-subtitle">You don’t have Platform access on this account.</p>
          </div>
          <div className="ft-header__actions">
            <button className="ft-btn ft-btn--ghost" type="button" onClick={() => nav("/unauthorized")}>
              Unauthorized
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!inPlatformRoute) {
    return (
      <div className="ft-page">
        <div className="ft-header">
          <div className="ft-header__text">
            <h1 className="ft-title">Platform</h1>
            <p className="ft-subtitle">
              This page must be accessed under <b>/platform</b>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ft-page">
      <div className="ft-header" style={{ marginBottom: 14 }}>
        <div className="ft-header__text">
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--muted)",
              marginBottom: 6,
            }}
          >
            Platform
          </div>
          <h1 className="ft-title">Admin Home</h1>
          <p className="ft-subtitle">
            Welcome back. You’re signed in as <b>{roleLabel(platformRole)}</b>.
            <br />
            This page should surface what needs attention across stores, users, employments, and billing.
          </p>
        </div>

        <div className="ft-header__actions">
          <button className="ft-btn ft-btn--primary" type="button" onClick={() => nav("/platform/business-units")}>
            Business Units
          </button>
          <button className="ft-btn ft-btn--ghost" type="button" onClick={() => nav("/platform/users")}>
            Users
          </button>
        </div>
      </div>

      <div className="ft-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
        <div className="ft-card">
          <div className="ft-card__header">
            <div className="ft-card__headerTitle">Business Units</div>
            <div className="ft-card__headerSub">Stores / locations</div>
          </div>
          <div className="ft-card__body">
            <div className="ft-stat">{loading ? "—" : formatNumber(stats.businessUnits ?? 0)}</div>
            <div className="ft-hint">Platform-visible dealer groups and stores.</div>
          </div>
        </div>

        <div className="ft-card">
          <div className="ft-card__header">
            <div className="ft-card__headerTitle">Platform Users</div>
            <div className="ft-card__headerSub">Admins & support</div>
          </div>
          <div className="ft-card__body">
            <div className="ft-stat">{loading ? "—" : formatNumber(stats.platformUsers ?? 0)}</div>
            <div className="ft-hint">Internal admin and support access.</div>
          </div>
        </div>

        <div className="ft-card">
          <div className="ft-card__header">
            <div className="ft-card__headerTitle">App Users</div>
            <div className="ft-card__headerSub">Store staff identities</div>
          </div>
          <div className="ft-card__body">
            <div className="ft-stat">{loading ? "—" : formatNumber(stats.appUsers ?? 0)}</div>
            <div className="ft-hint">Staff accounts available for employments and support workflows.</div>
          </div>
        </div>
      </div>

      <div
        className="ft-grid"
        style={{
          marginTop: 14,
          gridTemplateColumns: "minmax(0, 1.2fr) minmax(320px, 0.8fr)",
          alignItems: "start",
        }}
      >
        <div className="ft-card">
          <div className="ft-card__header">
            <div className="ft-card__headerTitle">Needs Attention</div>
            <div className="ft-card__headerSub">This should become your daily scan area.</div>
          </div>

          <div className="ft-card__body" style={{ display: "grid", gap: 10 }}>
            {attentionItems.map((item) => (
              <AttentionRow
                key={item.title}
                title={item.title}
                body={item.body}
                tone={item.tone}
              />
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gap: 14 }}>
          <div className="ft-card">
            <div className="ft-card__header">
              <div className="ft-card__headerTitle">What’s Next</div>
              <div className="ft-card__headerSub">Recommended platform build sequence.</div>
            </div>

            <div className="ft-card__body" style={{ display: "grid", gap: 10 }}>
              <AttentionRow
                title="1. Finish Business Unit management"
                body="Keep details read-only by default, support cleaner status handling, and eventually show store health and staff counts."
              />
              <AttentionRow
                title="2. Expand user support tooling"
                body="Next high-value additions are impersonation, employment editing, and audit visibility."
              />
              <AttentionRow
                title="3. Wire billing alerts"
                body="Trials ending, past-due accounts, and renewal timing should become first-class Platform dashboard signals."
              />
            </div>
          </div>

          <div className="ft-card">
            <div className="ft-card__header">
              <div className="ft-card__headerTitle">Quick Access</div>
              <div className="ft-card__headerSub">Secondary actions, not the focus of the page.</div>
            </div>

            <div className="ft-card__body" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="ft-btn ft-btn--ghost" type="button" onClick={() => nav("/platform/business-units")}>
                Open Business Units
              </button>
              <button className="ft-btn ft-btn--ghost" type="button" onClick={() => nav("/platform/users")}>
                Open Users
              </button>
              <button className="ft-btn ft-btn--ghost" type="button" onClick={() => nav("/platform/billing")}>
                Billing
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}